import {
  LastCompletedVisitFilterValue,
  VisitCountFilterValue,
  VisitFilterOperator,
} from './_types';

const OPERATORS: VisitFilterOperator[] = ['gt', 'lt', 'eq'];

export type VisitFilterValidationErrorKey =
  | 'operatorRequired'
  | 'countRequired'
  | 'timeframeRequired'
  | 'incomplete';

export const isValidVisitFilterOperator = (
  operator: unknown
): boolean =>
  typeof operator === 'string' && OPERATORS.includes(operator as VisitFilterOperator);

export const isValidVisitCountValue = (value: unknown): boolean =>
  typeof value === 'number' && Number.isFinite(value) && value >= 0;

export const isValidTimeframeDays = (
  timeframeDays: unknown,
  allowed: readonly number[]
): boolean =>
  typeof timeframeDays === 'number' && allowed.includes(timeframeDays);

export const isVisitCountFilterComplete = (
  value: VisitCountFilterValue | null | undefined,
  allowedTimeframeDays: readonly number[]
): boolean => {
  if (!value) {
    return false;
  }

  return (
    isValidVisitFilterOperator(value.operator) &&
    isValidVisitCountValue(value.value) &&
    isValidTimeframeDays(value.timeframeDays, allowedTimeframeDays)
  );
};

export const isLastCompletedVisitFilterComplete = (
  value: LastCompletedVisitFilterValue | null | undefined,
  allowedTimeframeDays: readonly number[]
): boolean => {
  if (!value) {
    return false;
  }

  return (
    isValidVisitFilterOperator(value.operator) &&
    isValidTimeframeDays(value.timeframeDays, allowedTimeframeDays)
  );
};

export const getVisitCountFilterValidationErrorKey = (
  value: VisitCountFilterValue | null | undefined,
  allowedTimeframeDays: readonly number[]
): VisitFilterValidationErrorKey | null => {
  if (!value || (!value.operator && value.value == null && !value.timeframeDays)) {
    return null;
  }

  if (isVisitCountFilterComplete(value, allowedTimeframeDays)) {
    return null;
  }

  if (!isValidVisitFilterOperator(value.operator)) {
    return 'operatorRequired';
  }

  if (!isValidVisitCountValue(value.value)) {
    return 'countRequired';
  }

  if (!isValidTimeframeDays(value.timeframeDays, allowedTimeframeDays)) {
    return 'timeframeRequired';
  }

  return 'incomplete';
};

export const getLastCompletedVisitFilterValidationErrorKey = (
  value: LastCompletedVisitFilterValue | null | undefined,
  allowedTimeframeDays: readonly number[]
): VisitFilterValidationErrorKey | null => {
  if (!value || (!value.operator && !value.timeframeDays)) {
    return null;
  }

  if (isLastCompletedVisitFilterComplete(value, allowedTimeframeDays)) {
    return null;
  }

  if (!isValidVisitFilterOperator(value.operator)) {
    return 'operatorRequired';
  }

  if (!isValidTimeframeDays(value.timeframeDays, allowedTimeframeDays)) {
    return 'timeframeRequired';
  }

  return 'incomplete';
};
